import React, { useState } from 'react';

const sectors = [
  {
    emoji: '⛏️', name: 'Mining & Extraction',
    desc: 'Underground connectivity, IoT automation, personnel tracking, fleet management, and safety monitoring systems for open-pit and underground operations.',
    tags: ['Connectivity', 'IoT', 'Tracking', 'Safety'],
  },
  {
    emoji: '🛢️', name: 'Oil & Gas',
    desc: 'Supply chain digitisation, compliance documentation management, asset tracking, field service platforms, and ERP integration for upstream and downstream operators.',
    tags: ['Supply Chain', 'Compliance', 'Asset Mgmt', 'ERP'],
  },
  {
    emoji: '📡', name: 'Telecommunications',
    desc: 'Network infrastructure design, systems integration, and operational software for Ghana\'s growing telecoms sector and its supply chain partners.',
    tags: ['Network', 'Systems', 'Integration'],
  },
  {
    emoji: '⚡', name: 'Energy & Utilities',
    desc: 'Remote monitoring, operational dashboards, and connectivity platforms for energy generation, distribution, and management companies across West Africa.',
    tags: ['Monitoring', 'Dashboards', 'Connectivity'],
  },
  {
    emoji: '🏗️', name: 'Industrial Supply Chain',
    desc: 'Inventory management, multi-site integration, client order portals, and logistics tracking for industrial consumables and equipment suppliers.',
    tags: ['Inventory', 'Portals', 'Logistics', 'Multi-site'],
  },
  {
    emoji: '🏢', name: 'Enterprise & Corporate',
    desc: 'End-to-end IT infrastructure, cybersecurity, cloud migration, and business intelligence platforms for growing Ghanaian and West African enterprises.',
    tags: ['IT Infra', 'Security', 'Cloud', 'BI'],
  },
];

export function Sectors() {
  const [hovered, setHovered] = useState(null);

  return (
    <section id="sectors" style={{ padding: '100px 5%', background: 'var(--navy)' }}>
      <div style={{ marginBottom: '3.5rem' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--electric)', marginBottom: '0.8rem' }}>
          Industries We Serve
        </div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.9rem,3.5vw,2.8rem)', fontWeight: 700, color: 'var(--white)', letterSpacing: '-0.8px', lineHeight: 1.15, marginBottom: '1rem' }}>
          Technology built around<br />your sector's real demands
        </h2>
        <p style={{ color: 'var(--slate)', fontSize: '1.05rem', maxWidth: '520px', lineHeight: 1.7 }}>
          We don't apply generic IT templates. We understand the specific challenges of each industry we work in.
        </p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1rem',
      }}>
        {sectors.map((s, i) => (
          <div key={i}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
            style={{
              background: 'var(--card-bg)',
              border: `1px solid ${hovered === i ? 'var(--electric)' : 'var(--card-border)'}`,
              borderRadius: '12px', padding: '1.8rem',
              transition: 'border-color 0.25s, transform 0.2s',
              transform: hovered === i ? 'translateY(-4px)' : 'translateY(0)',
              cursor: 'default',
            }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>{s.emoji}</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 600, color: 'var(--white)', marginBottom: '0.6rem' }}>{s.name}</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--slate)', lineHeight: 1.6, marginBottom: '1.1rem' }}>{s.desc}</div>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              {s.tags.map((t, j) => (
                <span key={j} style={{
                  fontSize: '0.68rem', fontWeight: 600,
                  background: 'rgba(0,180,216,0.07)', color: 'var(--electric)',
                  border: '1px solid rgba(0,180,216,0.15)',
                  padding: '0.18rem 0.55rem', borderRadius: '4px',
                }}>{t}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── PROCESS ────────────────────────────────────────────────────────────────

const steps = [
  {
    num: '01', title: 'Discovery & Audit',
    desc: 'We begin with a thorough audit of your current IT landscape, infrastructure, processes, and technology gaps — establishing a clear baseline before recommending anything.',
  },
  {
    num: '02', title: 'Roadmap Design',
    desc: 'We design a phased technology roadmap specific to your operations, priorities, and budget — a clear picture of what gets built, when, and why.',
  },
  {
    num: '03', title: 'Build & Deploy',
    desc: 'Our team executes each phase with precision — from network installation and software development to system integration and user onboarding.',
  },
  {
    num: '04', title: 'Support & Scale',
    desc: 'We remain your long-term partner — providing ongoing maintenance, support, and technology advisory as your business grows and your needs evolve.',
  },
];

export function Process() {
  return (
    <section id="process" style={{ padding: '100px 5%', background: 'var(--navy-mid)' }}>
      <div style={{ marginBottom: '3.5rem' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--electric)', marginBottom: '0.8rem' }}>
          How We Work
        </div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.9rem,3.5vw,2.8rem)', fontWeight: 700, color: 'var(--white)', letterSpacing: '-0.8px', lineHeight: 1.15, marginBottom: '1rem' }}>
          A structured approach<br />from day one
        </h2>
        <p style={{ color: 'var(--slate)', fontSize: '1.05rem', maxWidth: '500px', lineHeight: 1.7 }}>
          Every engagement follows a disciplined process that minimises risk and delivers measurable outcomes at each stage.
        </p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '0',
        position: 'relative',
      }}>
        {steps.map((s, i) => (
          <div key={i} style={{
            padding: '2rem 2.2rem 2rem 0',
            borderRight: i < steps.length - 1 ? '1px solid rgba(0,180,216,0.1)' : 'none',
            position: 'relative',
          }}>
            {i < steps.length - 1 && (
              <div style={{
                position: 'absolute', right: '-12px', top: '2rem',
                color: 'var(--electric)', fontSize: '1.2rem', opacity: 0.3,
                zIndex: 1,
              }}>→</div>
            )}
            <div style={{
              fontFamily: 'var(--font-display)', fontSize: '3rem', fontWeight: 800,
              color: 'var(--electric)', opacity: 0.12, lineHeight: 1, marginBottom: '0.6rem',
            }}>{s.num}</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', fontWeight: 600, color: 'var(--white)', marginBottom: '0.6rem' }}>{s.title}</div>
            <div style={{ fontSize: '0.87rem', color: 'var(--slate)', lineHeight: 1.65 }}>{s.desc}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
